/* CareerClarity.jsx — V3 JSON-driven Career Clarity for B.Tech & BBA/MBA.

   Source of truth (DO NOT modify in code):
     /data/btech_career_clarity_v3_ai_agent_ready.json
     /data/bba_mba_career_clarity_v3_ai_agent_ready.json

   Flow (from JSON `flow` array):
     Intro → Test 1 (15 q) → Theory 1 → Continue → ... → Test 4 → Theory 4 → Final Report

   Scoring (from JSON `scoring_logic`):
     For every selected option, add 1 to every tag in option.score_tags.
     Top tag = top recommendation; next two = alternatives.
     Tie-breaker: prefer tags repeated across multiple tests, else GENERAL_EXPLORER.

   Report sections (13, in this exact order — see spec).

   UI: playful & colorful student-friendly theme. Tapping an answer auto-advances
   after a short pause. ALL quiz content comes from the JSON — only UI copy here.
*/
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Loader2, BookOpen, Compass, Sparkles,
  Bot, ShieldCheck, GraduationCap, Briefcase, Lightbulb, ListChecks,
  TrendingUp, AlertCircle, Download, RotateCcw, Check, PartyPopper,
  ChevronRight, Cpu, Eye, HandHeart, Wrench, Map,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { QuizOptions, QuizSidePanel, QuestionDots, KeyHint, byId } from '../components/clarity/QuizStage';
import { saveTestResult } from '../lib/testResults';
import axios from 'axios';
import { cardToast } from '../lib/clarityCard';
import LexiTrigger from '../components/lexi/LexiTrigger';
import { ClaritySubjectsButton, ClaritySubjectsGateway } from '../components/ClaritySubjectLayer';
import { loadContent, isLocked } from '../lib/contentLoader';
import { useAuth } from '../lib/auth';
import { CompactClarityResult } from '../components/release/CompactClarityResult';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;
// Backend track key — bba-mba in URL maps to bba_mba on the server.
const SERVER_TRACK = { btech: 'btech', 'bba-mba': 'bba_mba' };

/* ───── Package config (only file names + visual accents — content is in JSON) ───── */
const PACKAGES = {
  btech: {
    jsonFile: 'btech_v3.json',
    short: 'B.Tech',
    eyebrow: 'After 10th / Inter — B.Tech Career Clarity',
    accent: 'from-teal-500 via-emerald-500 to-cyan-600',
    icon: GraduationCap,
  },
  'bba-mba': {
    jsonFile: 'bba_v3.json',
    short: 'BBA / MBA',
    eyebrow: 'After 10th / Inter — BBA/MBA Career Clarity',
    accent: 'from-amber-500 via-orange-500 to-rose-500',
    icon: Briefcase,
  },
};

/* Per-test playful pastel themes (rotate per testIdx 0..3) */
const TEST_THEMES = [
  {
    grad: 'from-teal-400 to-emerald-500',
    softBg: 'bg-teal-50', softBorder: 'border-teal-200', text: 'text-teal-700',
    selBorder: 'border-teal-500', selBg: 'bg-teal-50', selText: 'text-teal-900',
    badgeSel: 'bg-teal-500 text-white', badge: 'bg-teal-100 text-teal-700',
    hover: 'hover:border-teal-300 hover:bg-teal-50/60', dot: 'bg-teal-500',
    btn: 'bg-teal-500 hover:bg-teal-600',
    shadow: 'shadow-teal-200', keyHover: 'group-hover:border-teal-300 group-hover:text-teal-700',
  },
  {
    grad: 'from-amber-400 to-orange-500',
    softBg: 'bg-amber-50', softBorder: 'border-amber-200', text: 'text-amber-700',
    selBorder: 'border-amber-500', selBg: 'bg-amber-50', selText: 'text-amber-900',
    badgeSel: 'bg-amber-500 text-white', badge: 'bg-amber-100 text-amber-700',
    hover: 'hover:border-amber-300 hover:bg-amber-50/60', dot: 'bg-amber-500',
    btn: 'bg-amber-500 hover:bg-amber-600',
    shadow: 'shadow-amber-200', keyHover: 'group-hover:border-amber-300 group-hover:text-amber-700',
  },
  {
    grad: 'from-rose-400 to-pink-500',
    softBg: 'bg-rose-50', softBorder: 'border-rose-200', text: 'text-rose-700',
    selBorder: 'border-rose-500', selBg: 'bg-rose-50', selText: 'text-rose-900',
    badgeSel: 'bg-rose-500 text-white', badge: 'bg-rose-100 text-rose-700',
    hover: 'hover:border-rose-300 hover:bg-rose-50/60', dot: 'bg-rose-500',
    btn: 'bg-rose-500 hover:bg-rose-600',
    shadow: 'shadow-rose-200', keyHover: 'group-hover:border-rose-300 group-hover:text-rose-700',
  },
  {
    grad: 'from-indigo-400 to-violet-500',
    softBg: 'bg-indigo-50', softBorder: 'border-indigo-200', text: 'text-indigo-700',
    selBorder: 'border-indigo-500', selBg: 'bg-indigo-50', selText: 'text-indigo-900',
    badgeSel: 'bg-indigo-500 text-white', badge: 'bg-indigo-100 text-indigo-700',
    hover: 'hover:border-indigo-300 hover:bg-indigo-50/60', dot: 'bg-indigo-500',
    btn: 'bg-indigo-500 hover:bg-indigo-600',
    shadow: 'shadow-indigo-200', keyHover: 'group-hover:border-indigo-300 group-hover:text-indigo-700',
  },
];

/* ───── Strict validation ─────
   Confirms the JSON matches what the UI expects. Logs a clear error to console
   AND surfaces a banner to the student if anything is off.
*/
function validatePackage(pkg) {
  const errors = [];
  if (!pkg) return ['Package is empty.'];
  if (!Array.isArray(pkg.tests) || pkg.tests.length !== 4) {
    errors.push(`Expected exactly 4 tests, got ${pkg.tests?.length ?? 0}.`);
  }
  (pkg.tests || []).forEach((t, i) => {
    if (!Array.isArray(t.questions) || t.questions.length !== 15) {
      errors.push(`Test ${i + 1} (${t.test_id}) must have exactly 15 questions, has ${t.questions?.length}.`);
    }
    (t.questions || []).forEach((q) => {
      if (!Array.isArray(q.answer_options) || q.answer_options.length === 0) {
        errors.push(`Question ${q.id} has no answer_options.`);
      }
      (q.answer_options || []).forEach((opt) => {
        const hasWeighted = Array.isArray(opt.weighted_score_tags) && opt.weighted_score_tags.length > 0;
        const hasFlat = Array.isArray(opt.score_tags) && opt.score_tags.length > 0;
        if (!hasWeighted && !hasFlat) {
          errors.push(`Option ${q.id}.${opt.option_id} has no weighted_score_tags/score_tags.`);
        }
      });
    });
  });
  if (!Array.isArray(pkg.theory_after_each_test) || pkg.theory_after_each_test.length !== 4) {
    errors.push(`theory_after_each_test must contain 4 entries.`);
  }
  const branchTags = pkg.branch_tags || {};
  const profiles = Array.isArray(pkg.result_profiles) ? pkg.result_profiles : [];
  const profileTags = new Set(profiles.map((p) => p.tag));
  Object.keys(branchTags).forEach((tag) => {
    if (!profileTags.has(tag)) errors.push(`branch_tags has '${tag}' but no result_profile.`);
  });
  if (!profileTags.has('GENERAL_EXPLORER')) {
    errors.push(`result_profiles must include GENERAL_EXPLORER (fallback).`);
  }
  return errors;
}

/* ───── Score helpers (V4.1 weighted scoring, pure) ─────
   answers: { [questionId]: { option_id, weighted_score_tags, test_id } }
   raw[tag]  = Σ weight × test_weight  ·  norm[tag] = 100 × raw / max_possible[tag]
   discBoosts: { [tag]: +3 per discriminator pick } added to normalized scores.
*/
function scoreV4(answers, pkg, discBoosts = {}) {
  const raw = {};
  const perTest = {};
  const testWeights = Object.fromEntries((pkg?.tests || []).map((t) => [t.test_id, t.test_weight ?? 1.0]));
  Object.values(answers || {}).forEach((a) => {
    const tw = testWeights[a.test_id] ?? 1.0;
    const wst = a.weighted_score_tags || (a.score_tags || []).map((t) => ({ tag: t, weight: 1 }));
    wst.forEach(({ tag, weight }) => {
      raw[tag] = (raw[tag] || 0) + (weight || 1) * tw;
      if (!perTest[tag]) perTest[tag] = new Set();
      perTest[tag].add(a.test_id);
    });
  });
  const maxTable = pkg?.scoring_logic_v4?.normalization?.max_possible_per_tag || {};
  const norm = {};
  Object.keys(raw).forEach((tag) => {
    norm[tag] = maxTable[tag] ? (100 * raw[tag]) / maxTable[tag] : raw[tag];
  });
  Object.entries(discBoosts).forEach(([tag, b]) => { norm[tag] = (norm[tag] || 0) + b; });
  return { raw, norm, perTest };
}

/* Rank by normalized score; meta tags never win (kept separately). */
function rankV4(answers, pkg, discBoosts = {}) {
  const { norm, perTest } = scoreV4(answers, pkg, discBoosts);
  const meta = new Set(pkg?.scoring_logic_v4?.meta_tags || []);
  const all = Object.entries(norm)
    .map(([tag, score]) => ({ tag, score, testsCount: (perTest[tag] || new Set()).size, isMeta: meta.has(tag) }))
    .sort((a, b) => b.score - a.score || b.testsCount - a.testsCount || a.tag.localeCompare(b.tag));
  return { all, real: all.filter((r) => !r.isMeta) };
}

/* Top + 2 alternatives (real branches only). Explorer fallback if no branch > 35. */
function pickResultsV4(answers, pkg, profilesByTag, discBoosts = {}) {
  const { real } = rankV4(answers, pkg, discBoosts);
  if (!real.length || (real[0].score || 0) <= 35) {
    return {
      top: profilesByTag.GENERAL_EXPLORER,
      alts: real.slice(0, 2).map((r) => profilesByTag[r.tag]).filter(Boolean),
      fellBack: true,
      ranking: real,
    };
  }
  return {
    top: profilesByTag[real[0].tag] || profilesByTag.GENERAL_EXPLORER,
    alts: real.slice(1, 3).map((r) => profilesByTag[r.tag]).filter(Boolean),
    fellBack: false,
    ranking: real,
  };
}

const clusterOfTag = (pkg, tag) =>
  Object.entries(pkg?.scoring_logic_v4?.clusters || {}).find(([, c]) => (c.tags || []).includes(tag))?.[0] || null;

/* Cluster with highest mean of member-tag normalized scores (for progress teasers). */
function clusterLeader(answers, pkg) {
  const { norm } = scoreV4(answers, pkg);
  const clusters = pkg?.scoring_logic_v4?.clusters || {};
  let best = null;
  Object.entries(clusters).forEach(([key, c]) => {
    const tags = c.tags || [];
    const mean = tags.reduce((s, t) => s + (norm[t] || 0), 0) / (tags.length || 1);
    if (!best || mean > best.mean) best = { key, mean };
  });
  if (!best || best.mean <= 0) return null;
  const names = pkg?.progress_teasers?.cluster_display_names || {};
  return names[best.key] || clusters[best.key]?.name || best.key;
}

/* Discriminator trigger: gap<6, or same-cluster gap<10 → that cluster's questions (max 2 clusters). */
function discriminatorQuestions(answers, pkg) {
  const { real } = rankV4(answers, pkg);
  if (real.length < 2) return null;
  const gap = real[0].score - real[1].score;
  const c0 = clusterOfTag(pkg, real[0].tag);
  const c1 = clusterOfTag(pkg, real[1].tag);
  const sameCluster = c0 && c0 === c1;
  if (gap < 6 || (sameCluster && gap < 10)) {
    const clusters = [...new Set([c0, c1].filter(Boolean))].slice(0, 2);
    const qs = clusters.flatMap((ck) => pkg?.discriminator_round?.clusters?.[ck] || []);
    return qs.length ? qs : null;
  }
  return null;
}

/* Confidence band from final_report_logic.confidence_bands (gap between #1 and #2). */
function confidenceBand(gap, bands) {
  const msgOf = (s) => { const m = String(s || '').match(/['‘’]([^'‘’]+)['‘’]/); return m ? m[1] : String(s || ''); };
  if (gap >= 12) return { label: 'High confidence', msg: msgOf(bands?.high) };
  if (gap >= 6) return { label: 'Medium confidence', msg: msgOf(bands?.medium) };
  return { label: 'Exploratory', msg: msgOf(bands?.exploratory) };
}

/* Options always shown in letter order (A, B, C, D, E). */
const orderedOptions = (arr) => (arr || []).map((o) => ({ ...o, id: o.option_id })).sort(byId);

/* Tests where the top tag scored, for the "why this path matches you" line. */
function whyMatched(topTag, answers, tests) {
  const idsHit = new Set();
  Object.values(answers).forEach((a) => {
    const tags = (a.weighted_score_tags || []).map((w) => w.tag).concat(a.score_tags || []);
    if (tags.includes(topTag)) idsHit.add(a.test_id);
  });
  return tests.filter((t) => idsHit.has(t.test_id)).map((t) => t.title);
}

/* ───── UI atoms (playful & colorful) ───── */

/* Soft pastel decorative blobs for page backgrounds */
const Blobs = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-teal-200/40 blur-3xl" />
    <div className="absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-amber-200/40 blur-3xl" />
    <div className="absolute bottom-0 left-1/4 w-64 h-64 rounded-full bg-rose-200/30 blur-3xl" />
  </div>
);

/* Journey rail — the 4 tests as pills (done / current / upcoming) */
const JourneyRail = ({ tests, testIdx }) => (
  <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="cc-journey-rail">
    {tests.map((t, i) => {
      const tt = TEST_THEMES[i];
      const done = i < testIdx;
      const cur = i === testIdx;
      return (
        <div
          key={t.test_id}
          className={`flex items-center gap-2 shrink-0 rounded-full pl-1.5 pr-3.5 py-1.5 text-xs font-bold border transition-colors ${
            cur ? `bg-gradient-to-r ${tt.grad} text-white border-transparent shadow-md` : done ? `${tt.softBg} ${tt.text} ${tt.softBorder}` : 'bg-white text-gray-400 border-gray-200'
          }`}
          data-testid={`cc-rail-${i}`}
        >
          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black ${cur ? 'bg-white/25' : done ? 'bg-white' : 'bg-gray-100'}`}>
            {done ? <Check size={11} strokeWidth={3} /> : i + 1}
          </span>
          <span className="whitespace-nowrap">{t.title.replace(/\s*test$/i, '')}</span>
        </div>
      );
    })}
  </div>
);

const SectionCard = ({ icon: Icon, title, accent = 'teal', children, testId, id }) => {
  const palette = {
    teal: { card: 'border-teal-200 bg-teal-50/50', icon: 'bg-teal-500' },
    amber: { card: 'border-amber-200 bg-amber-50/50', icon: 'bg-amber-500' },
    purple: { card: 'border-purple-200 bg-purple-50/50', icon: 'bg-purple-500' },
    rose: { card: 'border-rose-200 bg-rose-50/50', icon: 'bg-rose-500' },
    emerald: { card: 'border-emerald-200 bg-emerald-50/50', icon: 'bg-emerald-500' },
    cyan: { card: 'border-cyan-200 bg-cyan-50/50', icon: 'bg-cyan-500' },
    indigo: { card: 'border-indigo-200 bg-indigo-50/50', icon: 'bg-indigo-500' },
  };
  const pal = palette[accent] || palette.teal;
  return (
    <section id={id} className={`rounded-3xl border-2 ${pal.card} p-5 md:p-7 shadow-sm scroll-mt-48`} data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <span className={`w-10 h-10 rounded-2xl ${pal.icon} text-white flex items-center justify-center shadow-sm shrink-0`}>
          <Icon size={19} />
        </span>
        <h3 className="text-lg md:text-xl font-extrabold text-gray-900">{title}</h3>
      </div>
      <div className="text-gray-800 leading-relaxed">{children}</div>
    </section>
  );
};

const Pills = ({ items, tone = 'teal' }) => {
  const tones = {
    teal: 'bg-teal-100/80 text-teal-900 border-teal-200',
    purple: 'bg-purple-100/80 text-purple-900 border-purple-200',
    emerald: 'bg-emerald-100/80 text-emerald-900 border-emerald-200',
  };
  return (
    <div className="flex flex-wrap gap-2">
      {(items || []).map((x, i) => (
        <span key={i} className={`text-xs md:text-sm px-3 py-1.5 rounded-full border font-medium ${tones[tone] || tones.teal}`}>{x}</span>
      ))}
    </div>
  );
};

const Bullets = ({ items }) => (
  <ul className="space-y-2">
    {(items || []).map((x, i) => (
      <li key={i} className="flex gap-2.5 text-sm md:text-base text-gray-800">
        <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center mt-0.5 shrink-0">
          <ChevronRight size={12} />
        </span>
        <span>{x}</span>
      </li>
    ))}
  </ul>
);

/* Glossary terms in question text → tap/hover tooltip (existing shadcn Tooltip). */
const GlossaryText = ({ text, terms }) => {
  if (!terms || !text) return <>{text}</>;
  const keys = Object.keys(terms).sort((a, b) => b.length - a.length);
  if (!keys.length) return <>{text}</>;
  const re = new RegExp(`\\b((?:${keys.map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})s?)\\b`, 'gi');
  const lower = Object.fromEntries(keys.map((k) => [k.toLowerCase(), terms[k]]));
  const parts = String(text).split(re);
  return (
    <>
      {parts.map((p, i) => {
        const key = typeof p === 'string' ? p.toLowerCase() : '';
        const def = lower[key] || lower[key.replace(/s$/, '')];
        if (!def) return <React.Fragment key={i}>{p}</React.Fragment>;
        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="underline decoration-dotted underline-offset-2 cursor-help">{p}</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">{def}</TooltipContent>
          </Tooltip>
        );
      })}
    </>
  );
};

/* AI Awareness banner — uses package_level_ai_awareness_view */
const AiAwarenessBlock = ({ awareness, compact = false }) => {
  if (!awareness) return null;
  return (
    <div className={`rounded-3xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 ${compact ? 'p-4' : 'p-5 md:p-7'}`} data-testid="awareness-block">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-8 h-8 rounded-xl bg-indigo-500 text-white flex items-center justify-center"><Bot size={16} /></span>
        <p className="text-[11px] font-bold text-indigo-700 uppercase tracking-widest">AI Awareness</p>
      </div>
      <p className="text-sm md:text-base text-gray-900 font-medium mb-3">{awareness.main_message}</p>
      {!compact && (
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          <div className="bg-white/70 rounded-2xl border border-purple-100 p-3">
            <p className="font-semibold text-indigo-800 mb-1.5">What AI automates</p>
            <Pills items={awareness.what_ai_automates} tone="purple" />
          </div>
          <div className="bg-white/70 rounded-2xl border border-emerald-100 p-3">
            <p className="font-semibold text-indigo-800 mb-1.5">What humans must do</p>
            <Pills items={awareness.what_humans_must_do} tone="emerald" />
          </div>
        </div>
      )}
      {awareness.ai_agents_as_digital_coworkers && (
        <p className="text-xs md:text-sm text-gray-700 mt-3 italic">{awareness.ai_agents_as_digital_coworkers}</p>
      )}
      {awareness.golden_rule && (
        <div className="mt-3 px-4 py-2.5 rounded-2xl bg-amber-50 border-2 border-amber-200 text-sm text-amber-900">
          <b>Golden rule:</b> {awareness.golden_rule}
        </div>
      )}
    </div>
  );
};

/* ───── Main component ───── */
const CareerClarity = () => {
  const { pathway } = useParams();
  const navigate = useNavigate();
  const cfg = PACKAGES[pathway];
  const { user } = useAuth();
  // Scope all progress keys by user_id so a new user never resumes another
  // person's saved answers/reports on the same browser.
  const userScope = user && user !== false ? (user.user_id || user.email || 'anon') : 'anon';

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Backend attempt counter for the after_10th_inter clarity quiz.
  // Max 2 attempts per track. When attempts.used >= 2, intro shows a
  // "both attempts used" panel and blocks new submissions.
  const [attempts, setAttempts] = useState(null); // { used, max, latest }
  const [submitError, setSubmitError] = useState('');

  /* Flow:
       phase = 'intro' | 'questions' | 'theory' | 'report'
       testIdx 0..3  ·  qIdx 0..14
  */
  const [phase, setPhase] = useState('intro');
  const [testIdx, setTestIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});   // { [qid]: {option_id, weighted_score_tags, test_id} }
  const [discAnswers, setDiscAnswers] = useState({}); // { [qid]: {option_id, tag} }
  const [discIdx, setDiscIdx] = useState(0);
  const [audience, setAudience] = useState(null); // BBA/MBA audience_gate pick
  const [advancing, setAdvancing] = useState(false); // lock during auto-advance pause
  const advanceTimer = useRef(null);
  const reportRef = useRef(null);                 // PDF capture target (report phase)
  const [downloading, setDownloading] = useState(false);
  const storageKey = pathway ? `cc_v4_progress_${pathway}_${userScope}` : null;

  /* Load & validate JSON */
  useEffect(() => {
    if (!cfg) {
      setError('Unknown pathway. Use /career-clarity/btech or /career-clarity/bba-mba.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    loadContent(cfg.jsonFile)
      .then((data) => {
        if (cancelled) return;
        if (!data) {
          setError('Could not load the quiz package (network error). Please refresh.');
          setLoading(false);
          return;
        }
        if (isLocked(data)) {
          setError('This quiz isn\'t open right now — reload the page, or come back tomorrow if you\'ve used today\'s 3 free paths.');
          setLoading(false);
          return;
        }
        const errs = validatePackage(data);
        if (errs.length) {
          // eslint-disable-next-line no-console
          console.error('[CareerClarity] V3 package validation failed:', errs);
          setError(`This quiz package is invalid: ${errs[0]}`);
          setLoading(false);
          return;
        }
        setPkg(data);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(`Could not load the quiz package (${e.message}).`);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [cfg]);

  /* Resume from localStorage */
  useEffect(() => {
    if (!pkg || !storageKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
      if (saved && saved.answers && typeof saved.testIdx === 'number') {
        setAnswers(saved.answers);
        setTestIdx(saved.testIdx);
        setQIdx(saved.qIdx || 0);
        setPhase(saved.phase || 'questions');
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg]);

  /* Persist progress every change */
  useEffect(() => {
    if (!pkg || !storageKey) return;
    if (phase === 'intro') return; // nothing to save yet
    try {
      localStorage.setItem(storageKey, JSON.stringify({ phase, testIdx, qIdx, answers, discAnswers, discIdx, audience }));
    } catch { /* ignore quota */ }
  }, [pkg, storageKey, phase, testIdx, qIdx, answers, discAnswers, discIdx, audience]);

  /* Scroll-to-top on phase / test changes */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [phase, testIdx]);

  /* Clear any pending auto-advance timer on unmount */
  useEffect(() => () => { if (advanceTimer.current) clearTimeout(advanceTimer.current); }, []);

  /* Fetch backend attempts counter for the 2-attempt cap (per track). */
  useEffect(() => {
    if (!user || user === false || user.is_guest) return;
    const serverTrack = SERVER_TRACK[pathway];
    if (!serverTrack) return;
    axios.get(`${API}/api/paths/after_10th_inter/attempts?track=${serverTrack}`)
      .then((r) => setAttempts(r.data))
      .catch(() => setAttempts({ used: 0, max: 2, latest: null }));
  }, [user, pathway]);

  /* Save final report to backend on report phase (server enforces 2-attempt cap). */
  useEffect(() => {
    if (phase !== 'report' || !pkg) return;
    // Local save (kept for backward-compat)
    try {
      saveTestResult(pathway, `${pathway.toUpperCase()}_CAREER_CLARITY_V4`, {
        title: `${cfg.short} Career Clarity Report (V4)`,
        completedAt: new Date().toISOString(),
        answers,
        ranking: rankV4(answers, pkg).real,
      });
    } catch { /* ignore */ }
    // Server-side submit (enforces 2/track cap, persists to quiz_attempts).
    if (!user || user.is_guest) return;
    const serverTrack = SERVER_TRACK[pathway];
    if (!serverTrack) return;
    const ranking = rankV4(answers, pkg).real;
    const topTag = ranking?.[0]?.tag;
    const topProfile = (pkg.result_profiles || []).find((p) => p.tag === topTag) || {};
    axios.post(`${API}/api/paths/after_10th_inter/submit`, {
      track: serverTrack,
      answers,
      result_profile: { tag: topTag, title: topProfile.title || '' },
    }).then((r) => {
      setAttempts((a) => ({ ...(a || {}), used: (r.data.attempt_no || 1) }));
      cardToast();
    }).catch((e) => {
      if (e?.response?.status === 429) {
        setSubmitError("You've already used all attempts on this track. Report shown is from your last save.");
      }
    });
  }, [phase, pkg, pathway, cfg, answers]);

  /* ── Derived state ── */
  const currentTest = pkg?.tests[testIdx];
  const currentQ = currentTest?.questions[qIdx];
  const isLastTest = testIdx === 3;
  const theme = TEST_THEMES[testIdx] || TEST_THEMES[0];
  const profilesByTag = useMemo(() => {
    if (!pkg) return {};
    return Object.fromEntries((pkg.result_profiles || []).map((p) => [p.tag, p]));
  }, [pkg]);

  /* ── Handlers ── */
  const handleNextQ = useCallback(() => {
    if (!currentTest) return;
    if (qIdx < currentTest.questions.length - 1) {
      setQIdx(qIdx + 1);
    } else {
      setPhase('theory');
    }
  }, [currentTest, qIdx]);

  /* Tap an answer → save it, show the tick, then auto-advance after a short pause */
  const handleSelect = useCallback((opt) => {
    if (!currentQ || !currentTest || advancing) return;
    setAnswers((prev) => ({
      ...prev,
      [currentQ.id]: {
        option_id: opt.option_id,
        weighted_score_tags: opt.weighted_score_tags || [],
        score_tags: opt.score_tags || [],
        test_id: currentTest.test_id,
      },
    }));
    setAdvancing(true);
    advanceTimer.current = setTimeout(() => {
      setAdvancing(false);
      handleNextQ();
    }, 550);
  }, [currentQ, currentTest, advancing, handleNextQ]);

  const handleBack = () => {
    if (advanceTimer.current) { clearTimeout(advanceTimer.current); setAdvancing(false); }
    if (phase === 'questions' && qIdx > 0) {
      setQIdx(qIdx - 1);
    } else if (phase === 'theory') {
      setPhase('questions');
      setQIdx(currentTest.questions.length - 1);
    } else if (phase === 'questions' && qIdx === 0 && testIdx > 0) {
      // Allow back to previous test theory
      setTestIdx(testIdx - 1);
      setPhase('theory');
    } else if (phase === 'questions' && qIdx === 0 && testIdx === 0) {
      setPhase(pkg.audience_gate?.ask_before_test_1 ? 'gate' : 'intro');
    } else if (phase === 'gate') {
      setPhase('intro');
    } else if (phase === 'discriminator') {
      setTestIdx(3);
      setPhase('theory');
    }
  };

  const BackLink = ({ onClick, to, label = 'Back', testId }) => {
    const cls = 'inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-teal-700 transition-colors mb-4';
    return to ? (
      <Link to={to} className={cls} data-testid={testId}><ArrowLeft size={15} /> {label}</Link>
    ) : (
      <button type="button" onClick={onClick} className={cls} data-testid={testId}><ArrowLeft size={15} /> {label}</button>
    );
  };

  const handleContinueFromTheory = () => {
    if (testIdx < 3) {
      setTestIdx(testIdx + 1);
      setQIdx(0);
      setPhase('questions');
    } else {
      // V4 discriminator round: top-two too close → 2 tie-breaker questions per cluster.
      const discQs = discriminatorQuestions(answers, pkg);
      if (discQs && Object.keys(discAnswers).length === 0) {
        setDiscIdx(0);
        setPhase('discriminator');
      } else {
        setPhase('report');
      }
    }
  };

  const handleStartOver = () => {
    if (advanceTimer.current) { clearTimeout(advanceTimer.current); setAdvancing(false); }
    if (storageKey) localStorage.removeItem(storageKey);
    setAnswers({});
    setDiscAnswers({});
    setDiscIdx(0);
    setAudience(null);
    setTestIdx(0);
    setQIdx(0);
    setPhase('intro');
  };

  const handleDownload = async () => {
    if (!reportRef.current || downloading) return;
    setDownloading(true);
    try {
      // Lazy-load to keep main bundle lean.
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas'),
        import('jspdf').then((m) => ({ default: m.jsPDF })),
      ]);

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#FFF9F1',
        windowWidth: 1100,                              // force consistent layout
        ignoreElements: (el) => el?.classList?.contains?.('cc-no-print'),
      });

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
      const pageW = pdf.internal.pageSize.getWidth();   // 210
      const pageH = pdf.internal.pageSize.getHeight();  // 297
      const margin = 10;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL('image/png');

      let heightLeft = imgH;
      let position = margin;
      pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH, undefined, 'FAST');
      heightLeft -= (pageH - margin * 2);

      while (heightLeft > 0) {
        position = margin - (imgH - heightLeft);
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgW, imgH, undefined, 'FAST');
        heightLeft -= (pageH - margin * 2);
      }

      pdf.save(`career-clarity-${pathway}-report.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  /* ── Loading / error states ── */
  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center bg-[#FFF9F1]">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unknown career-clarity pathway</h1>
          <p className="text-gray-600 mb-4">Try one of:</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/career-clarity/btech" className="text-teal-700 font-semibold underline">B.Tech</Link>
            <Link to="/career-clarity/bba-mba" className="text-amber-700 font-semibold underline">BBA / MBA</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500 bg-[#FFF9F1]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading career-clarity package…
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FFF9F1]">
        <div className="max-w-md text-center bg-white border-2 border-rose-200 rounded-3xl p-6">
          <AlertCircle className="text-rose-600 mx-auto mb-3" size={28} />
          <h1 className="text-lg font-bold text-gray-900 mb-2">Quiz package error</h1>
          <p className="text-sm text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  /* ───────── INTRO ───────── */
  if (phase === 'intro') {
    const Icon = cfg.icon;
    const steps = [
      { icon: ListChecks, label: 'Take 4 short tests', sub: 'Quick taps each — no marks, no pass/fail' },
      { icon: BookOpen, label: 'Read a tiny tip', sub: 'A short learning note after every test' },
      { icon: PartyPopper, label: 'Get your report', sub: 'Your personalised career-clarity report' },
    ];
    // Block if user has already used both attempts (server-enforced cap).
    // Per v3 spec: disable Retake, keep ONLY "View my result".
    // Admins bypass — they always have full access.
    if (attempts && !attempts.is_admin && attempts.used >= attempts.max) {
      const handleViewResult = () => {
        // Prefer the server-saved answers from the latest attempt
        const savedAnswers = attempts?.latest?.answers_json;
        if (savedAnswers && Object.keys(savedAnswers).length > 0) {
          setAnswers(savedAnswers);
          setPhase('report');
          return;
        }
        // Fallback: localStorage (same browser only)
        try {
          const cached = JSON.parse(localStorage.getItem(storageKey) || 'null');
          if (cached?.answers && Object.keys(cached.answers).length > 0) {
            setAnswers(cached.answers);
            setPhase('report');
          }
        } catch { /* ignore */ }
      };
      const hasSavedResult =
        !!attempts?.latest?.answers_json && Object.keys(attempts.latest.answers_json).length > 0;
      return (
        <div className="min-h-screen bg-[#FFF9F1]">
          <Navbar />
          <section className="pt-32 pb-16 px-4">
            <div className="max-w-2xl mx-auto bg-white border border-amber-200 rounded-3xl p-8 text-center shadow-sm" data-testid="cc-attempts-blocked">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3">
                <Icon size={13} /> {cfg.eyebrow}
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-amber-900 mb-2">You&apos;ve used all attempts</h1>
              <p className="text-sm text-amber-800 mb-5">
                The Career Clarity quiz is capped at <strong>Attempt {attempts.max} of {attempts.max}</strong> per track to keep your result meaningful.
                Your saved report is below.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {hasSavedResult && (
                  <Button onClick={handleViewResult} className="bg-amber-600 hover:bg-amber-700 text-white font-bold" data-testid="cc-view-my-result">
                    View my result
                  </Button>
                )}
                <Button asChild variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="cc-back-to-tracks">
                  <Link to="/career-clarity"><ArrowLeft size={14} className="mr-1.5" /> Back</Link>
                </Button>
                <Button asChild variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="cc-back-to-dashboard">
                  <Link to="/dashboard">Back to dashboard</Link>
                </Button>
              </div>
            </div>
          </section>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <section className={`relative px-4 md:px-6 pt-32 pb-16 md:pt-36 md:pb-20 bg-gradient-to-br ${cfg.accent} text-white rounded-b-[2.5rem] overflow-hidden`}>
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/10" aria-hidden="true" />
          <div className="absolute bottom-0 left-10 w-32 h-32 rounded-full bg-white/10" aria-hidden="true" />
          <div className="max-w-3xl mx-auto relative">
            <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
              <Link to="/career-clarity" className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/85 hover:text-white transition-colors" data-testid="cc-intro-back">
                <ArrowLeft size={15} /> Back
              </Link>
              <Link to="/career-clarity/after-inter/guide" className="inline-flex items-center gap-1.5 text-xs font-bold bg-white/20 backdrop-blur px-3 py-1.5 rounded-full hover:bg-white/30 transition-colors" data-testid="cc-intro-guide-link">
                Study, Skills &amp; Career Clarity guide →
              </Link>
            </div>
            <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              <Icon size={13} /> {cfg.eyebrow}
            </span>
            <h1 className="text-3xl md:text-5xl font-black leading-tight mb-3 cc-pop-in" data-testid="cc-intro-title">
              {pkg.package_name}
            </h1>
            <p className="text-white/90 text-base md:text-lg max-w-2xl">
              A four-test self-discovery journey. No right or wrong answers — only what fits you.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5 text-sm">
              <span className="bg-white/20 backdrop-blur px-3.5 py-1.5 rounded-full font-semibold">4 short tests</span>
              <span className="bg-white/20 backdrop-blur px-3.5 py-1.5 rounded-full font-semibold">No marks. No pass/fail.</span>
              <span className="bg-white/20 backdrop-blur px-3.5 py-1.5 rounded-full font-semibold">Personalised report at the end</span>
              {attempts && (
                <span className="bg-white/30 backdrop-blur px-3.5 py-1.5 rounded-full font-semibold" data-testid="cc-attempt-counter">
                  Attempt {Math.min(attempts.used + 1, attempts.max)} of {attempts.max}
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 max-w-3xl mx-auto -mt-8 relative z-10">
          {/* How it works — 3 playful step cards */}
          <div className="grid sm:grid-cols-3 gap-3" data-testid="cc-step-cards">
            {steps.map((s, i) => {
              const t = TEST_THEMES[i];
              const SIcon = s.icon;
              return (
                <div key={i} className={`bg-white rounded-3xl border-2 ${t.softBorder} p-4 shadow-sm cc-pop-in`} style={{ animationDelay: `${i * 90}ms` }}>
                  <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${t.grad} text-white flex items-center justify-center mb-2.5`}>
                    <SIcon size={18} />
                  </div>
                  <p className="font-extrabold text-gray-900 text-sm">{i + 1}. {s.label}</p>
                  <p className="text-xs text-gray-500 mt-1">{s.sub}</p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="px-4 md:px-6 py-8 max-w-3xl mx-auto space-y-5">
          {pkg.student_approach && typeof pkg.student_approach === 'object' && (
            <div className="bg-white border-2 border-gray-100 rounded-3xl p-5 md:p-6 shadow-sm">
              <h2 className="font-extrabold text-gray-900 mb-3 flex items-center gap-2 text-base">
                <Sparkles size={17} className="text-amber-500" /> Before you start
              </h2>
              <ol className="grid sm:grid-cols-2 gap-2.5 text-sm text-gray-700">
                {Object.values(pkg.student_approach).map((line, i) => (
                  <li key={i} className="flex gap-2 bg-amber-50/60 border border-amber-100 rounded-2xl px-3 py-2">
                    <span className="font-extrabold text-amber-600 shrink-0 whitespace-nowrap">{i + 1}.</span><span className="min-w-0">{line}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="bg-white border-2 border-gray-100 rounded-3xl p-5 md:p-6 shadow-sm">
            <h2 className="font-extrabold text-gray-900 mb-4 flex items-center gap-2"><Compass size={18} className="text-teal-600" /> The 4 tests</h2>
            <ol className="space-y-3">
              {pkg.tests.map((t, i) => {
                const tt = TEST_THEMES[i];
                return (
                  <li key={t.test_id} className={`flex items-start gap-3 text-sm md:text-base rounded-2xl border ${tt.softBorder} ${tt.softBg} px-4 py-3`}>
                    <span className={`w-7 h-7 rounded-full bg-gradient-to-br ${tt.grad} text-white text-xs font-extrabold flex items-center justify-center mt-0.5 shrink-0`}>{i + 1}</span>
                    <div>
                      <p className="font-bold text-gray-900">{t.title}</p>
                      {t.test_goal && <p className="text-xs text-gray-500 mt-0.5">{t.test_goal}</p>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>

          <AiAwarenessBlock awareness={pkg.package_level_ai_awareness_view} />

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={() => setPhase(pkg.audience_gate?.ask_before_test_1 && !audience ? 'gate' : 'questions')}
              className="bg-teal-500 hover:bg-teal-600 text-white px-7 py-6 text-base rounded-2xl font-bold shadow-md shadow-teal-200 flex-1"
              data-testid="cc-start-btn"
            >
              <Icon size={16} className="mr-2" /> Start the journey
            </Button>
            <LexiTrigger
              variant="ghost"
              prefill={`I'm starting the ${cfg.short} career-clarity quiz. What should I know before I begin?`}
              label="Ask Lexi first"
              testId="cc-intro-lexi"
            />
          </div>
          {Object.keys(answers).length > 0 && (
            <button onClick={handleStartOver} className="text-xs text-gray-500 hover:text-gray-700 inline-flex items-center gap-1" data-testid="cc-clear-progress">
              <RotateCcw size={11} /> Clear saved progress & start fresh
            </button>
          )}
        </section>
      </div>
    );
  }

  /* ───────── AUDIENCE GATE (BBA/MBA only, before Test 1) ───────── */
  if (phase === 'gate') {
    const gate = pkg.audience_gate;
    const theme0 = TEST_THEMES[0];
    const gateOptions = orderedOptions(gate.options);
    return (
      <div className="min-h-screen bg-[#FFF9F1] relative">
        <Blobs />
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 md:px-6 pt-28 pb-10 md:pt-32 md:pb-12 relative">
          <BackLink onClick={handleBack} testId="cc-gate-back" />
          <div className="bg-white border border-gray-100 rounded-[1.75rem] shadow-xl shadow-gray-200/50 overflow-hidden cc-slide-in">
            <div className={`h-1.5 bg-gradient-to-r ${theme0.grad}`} />
            <div className="p-5 md:p-8">
              <span className={`inline-block text-[11px] uppercase tracking-widest font-extrabold mb-3 px-3 py-1 rounded-full ${theme0.badge}`}>
                Before we start
              </span>
              <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-snug mb-6" data-testid="cc-gate-question">
                {gate.question}
              </h2>
              <QuizOptions
                options={gateOptions}
                selectedId={null}
                onSelect={(opt) => { setAudience(opt.audience || opt.option_id); setPhase('questions'); }}
                accent={theme0}
                testIdPrefix="cc-gate-opt"
              />
              <p className="mt-5 text-xs text-gray-400 text-center">This only shapes how your final report is framed</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── QUESTIONS ───────── */
  if (phase === 'questions') {
    const selectedOptId = answers[currentQ.id]?.option_id;
    const displayOptions = orderedOptions(currentQ.answer_options);
    const totalQ = currentTest.questions.length;
    const answeredIds = new Set(currentTest.questions.filter((q) => answers[q.id]).map((q) => q.id));
    const glossary = pkg.student_glossary?.terms;
    return (
      <div className="min-h-screen bg-[#FFF9F1] relative">
        <Blobs />
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-28 pb-12 md:pt-32 relative">
          <JourneyRail tests={pkg.tests} testIdx={testIdx} />

          <div className="mt-5 grid md:grid-cols-[230px_1fr] gap-5 items-start">
            <aside className="md:sticky md:top-28 space-y-3">
              <QuizSidePanel
                grad={theme.grad}
                eyebrow={`Test ${testIdx + 1} of 4`}
                title={currentTest.title}
                index={qIdx}
                total={totalQ}
                testId="cc-progress-bar"
              />
              <QuestionDots ids={currentTest.questions.map((q) => q.id)} answered={answeredIds} currentId={currentQ.id} dotClass={theme.dot} />
            </aside>

            <main>
              <div key={currentQ.id} className="bg-white border border-gray-100 rounded-[1.75rem] shadow-xl shadow-gray-200/50 overflow-hidden cc-slide-in">
                <div className={`h-1.5 bg-gradient-to-r ${theme.grad}`} />
                <div className="p-5 md:p-8">
                  <span className={`inline-block text-[11px] uppercase tracking-widest font-extrabold mb-3 px-3 py-1 rounded-full ${theme.badge}`}>
                    Question {qIdx + 1}
                  </span>
                  <h2 className="text-xl md:text-[1.65rem] font-extrabold text-gray-900 leading-snug mb-6" data-testid={`cc-q-${currentQ.id}`}>
                    <TooltipProvider delayDuration={100}>
                      <GlossaryText text={currentQ.question} terms={glossary} />
                    </TooltipProvider>
                  </h2>
                  <QuizOptions
                    options={displayOptions}
                    selectedId={selectedOptId}
                    onSelect={handleSelect}
                    disabled={advancing}
                    accent={theme}
                    testIdPrefix={`cc-opt-${currentQ.id}`}
                    renderText={(opt) => (
                      <TooltipProvider delayDuration={100}>
                        <GlossaryText text={opt.text} terms={glossary} />
                      </TooltipProvider>
                    )}
                  />
                  <KeyHint last={displayOptions[displayOptions.length - 1]?.id} />
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="rounded-2xl border-2 bg-white"
                  data-testid="cc-back-btn"
                >
                  <ArrowLeft size={14} className="mr-1.5" /> Back
                </Button>
                <button onClick={handleStartOver} className="text-xs text-gray-400 hover:text-rose-600 inline-flex items-center gap-1" data-testid="cc-startover-mid">
                  <RotateCcw size={10} /> Start over
                </button>
              </div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── THEORY (between tests) ───────── */
  if (phase === 'theory') {
    const theory = pkg.theory_after_each_test.find((t) => t.test_id === currentTest.test_id);
    return (
      <div className="min-h-screen bg-[#FFF9F1] relative">
        <Blobs />
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 md:px-6 pt-28 pb-10 md:pt-32 md:pb-14 relative">
          <BackLink onClick={handleBack} label="Back to questions" testId="cc-theory-back" />
          {/* celebration strip */}
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${theme.grad} text-white text-xs font-extrabold uppercase tracking-widest mb-4 cc-pop-in`}>
            <PartyPopper size={13} /> Test {testIdx + 1} done!
          </div>
          <p className="text-[11px] uppercase tracking-widest text-emerald-700 font-bold mb-2">What you just discovered</p>
          <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-5" data-testid="cc-theory-title">{theory?.title}</h2>

          <div className={`bg-white border-2 ${theme.softBorder} rounded-[1.75rem] p-5 md:p-7 shadow-lg shadow-gray-200/60 cc-slide-in`}>
            <span className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${theme.grad} text-white flex items-center justify-center mb-3`}>
              <BookOpen size={18} />
            </span>
            <p className="text-base md:text-lg text-gray-800 leading-[1.8] whitespace-pre-line max-w-prose" data-testid="cc-theory-body">
              {theory?.content}
            </p>
            <div className={`mt-5 px-4 py-2.5 rounded-2xl ${theme.softBg} border ${theme.softBorder} text-xs md:text-sm ${theme.text} font-medium`}>
              Learning section — informational only. Not scored. Just read it and continue.
            </div>
            {(() => {
              const lead = clusterLeader(answers, pkg);
              return lead ? (
                <div className={`mt-3 px-4 py-2.5 rounded-2xl bg-white border ${theme.softBorder} text-xs md:text-sm text-gray-800`} data-testid="cc-teaser">
                  <span className="font-medium">Your strongest direction so far:</span> <b>{lead}</b>
                </div>
              ) : null;
            })()}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <LexiTrigger
              variant="ghost"
              prefill={`Quick question while we move on from ${currentTest.title} — `}
              label="Ask Lexi quickly"
              testId="quiz-theory-lexi"
            />
            <Button
              onClick={handleContinueFromTheory}
              className={`${isLastTest ? 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600' : theme.btn} text-white px-7 py-6 text-base rounded-2xl font-bold shadow-md`}
              data-testid="cc-continue-btn"
            >
              {isLastTest ? 'See my Career Clarity Report' : `Continue to ${pkg.tests[testIdx + 1]?.title}`}
              <ArrowRight size={15} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── DISCRIMINATOR ROUND (V4 tie-breaker) ───────── */
  if (phase === 'discriminator') {
    const discQs = discriminatorQuestions(answers, pkg) || [];
    const dq = discQs[discIdx];
    if (!dq) { setPhase('report'); return null; }
    const dTheme = TEST_THEMES[3];
    const displayOptions = orderedOptions(dq.answer_options);
    const selectedOptId = discAnswers[dq.id]?.option_id;
    const handleDiscSelect = (opt) => {
      if (advancing) return;
      const tag = opt.weighted_score_tags?.[0]?.tag;
      setDiscAnswers((prev) => ({ ...prev, [dq.id]: { option_id: opt.option_id, tag } }));
      setAdvancing(true);
      advanceTimer.current = setTimeout(() => {
        setAdvancing(false);
        if (discIdx < discQs.length - 1) setDiscIdx(discIdx + 1);
        else setPhase('report');
      }, 550);
    };
    return (
      <div className="min-h-screen bg-[#FFF9F1] relative">
        <Blobs />
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 md:px-6 pt-28 pb-10 md:pt-32 md:pb-12 relative">
          <BackLink onClick={handleBack} testId="cc-disc-back" />
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${dTheme.grad} text-white text-xs font-extrabold uppercase tracking-widest mb-4 cc-pop-in`}>
            <Sparkles size={13} /> Tie-breaker · {discIdx + 1} of {discQs.length}
          </div>
          <p className="text-sm text-gray-700 mb-4" data-testid="cc-disc-intro">{pkg.discriminator_round?.student_facing_intro}</p>
          <div key={dq.id} className="bg-white border border-gray-100 rounded-[1.75rem] shadow-xl shadow-gray-200/50 overflow-hidden cc-slide-in">
            <div className={`h-1.5 bg-gradient-to-r ${dTheme.grad}`} />
            <div className="p-5 md:p-8">
              <h2 className="text-xl md:text-2xl font-extrabold text-gray-900 leading-snug mb-6" data-testid={`cc-q-${dq.id}`}>
                <TooltipProvider delayDuration={100}>
                  <GlossaryText text={dq.question} terms={pkg.student_glossary?.terms} />
                </TooltipProvider>
              </h2>
              <QuizOptions
                options={displayOptions}
                selectedId={selectedOptId}
                onSelect={handleDiscSelect}
                disabled={advancing}
                accent={dTheme}
                testIdPrefix={`cc-opt-${dq.id}`}
              />
              <KeyHint last={displayOptions[displayOptions.length - 1]?.id} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── REPORT ───────── */
  if (phase === 'report') {
    const discBoosts = {};
    Object.values(discAnswers).forEach((a) => { if (a.tag) discBoosts[a.tag] = (discBoosts[a.tag] || 0) + 3; });
    const { top, alts, fellBack, ranking } = pickResultsV4(answers, pkg, profilesByTag, discBoosts);
    return <CompactClarityResult pathway={pathway} audience={audience} tags={ranking.slice(0, 3).map(r => r.tag)} fellBack={fellBack} />;
    const matchPct = Math.round(Math.min(100, ranking.find((r) => r.tag === top?.tag)?.score ?? ranking[0]?.score ?? 0));
    const gap = ranking.length > 1 ? ranking[0].score - ranking[1].score : 99;
    const band = confidenceBand(gap, pkg.final_report_logic?.confidence_bands);
    const gateOpt = (pkg.audience_gate?.options || []).find((o) => (o.audience || o.option_id) === audience);
    const matchedTests = top ? whyMatched(top.tag, answers, pkg.tests) : [];
    const branchNames = pkg.branch_tags || {};

    /* mini-nav chips → anchor ids (only sections that exist) */
    const navChips = [
      alts.length > 0 && { id: 'cc-alts', label: 'Other matches', icon: Compass },
      { id: 'cc-why', label: 'Why you', icon: Sparkles },
      top.what_this_means && { id: 'cc-meaning', label: 'Meaning', icon: BookOpen },
      top.what_students_study && { id: 'cc-study', label: 'Study', icon: GraduationCap },
      top.career_paths && { id: 'cc-careers', label: 'Careers', icon: Briefcase },
      top.how_ai_is_changing_this_field && { id: 'cc-ai', label: 'AI impact', icon: TrendingUp },
      top.beginner_roadmap && { id: 'cc-roadmap', label: 'Roadmap', icon: Map },
      top.mini_project_idea && { id: 'cc-project', label: 'Project', icon: Lightbulb },
      top.future_work_with_ai_agents && { id: 'cc-future', label: 'Future + AI', icon: Bot },
      top.reality_check && { id: 'cc-reality', label: 'Reality check', icon: Eye },
    ].filter(Boolean);

    const scrollTo = (id) => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
      <div className="min-h-screen bg-[#FFF9F1]" ref={reportRef}>
        <div className="cc-no-print">
          <Navbar />
        </div>
        {/* hero */}
        <section className={`relative px-4 md:px-6 pt-32 pb-10 md:pt-36 md:pb-14 bg-gradient-to-br ${cfg.accent} text-white rounded-b-[2.5rem] overflow-hidden`}>
          <div className="absolute -top-12 right-8 w-48 h-48 rounded-full bg-white/10" aria-hidden="true" />
          <div className="max-w-4xl mx-auto relative">
            <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
              <PartyPopper size={13} /> {cfg.short} · Career Clarity Report
            </span>
            <p className="text-white/80 text-sm font-semibold mb-1">Your top match is…</p>
            <h1 className="text-3xl md:text-5xl font-black leading-tight mb-2 cc-pop-in" data-testid="cc-report-top-title">{top.title}</h1>
            <p className="text-white/90 text-sm font-semibold" data-testid="cc-report-confidence">
              {matchPct}% match · {band.label}{band.msg ? ` — ${band.msg}` : ''}
            </p>
            {gateOpt?.report_adaptation && (
              <p className="text-white/80 text-xs mt-1" data-testid="cc-report-audience-frame">
                Framed for you: {gateOpt.report_adaptation.split(';')[0]}
              </p>
            )}
            {top.also_includes?.length > 0 && (
              <p className="text-white/90 text-sm">
                Also includes: {top.also_includes.join(' · ')}
              </p>
            )}
            {fellBack && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs bg-white/15 backdrop-blur px-3 py-1 rounded-full">
                <Compass size={11} /> Your answers were spread across areas — we suggest exploring as a generalist for now.
              </p>
            )}
          </div>
        </section>

        {/* sticky mini-nav */}
        <div className="cc-no-print lxt-sticky-nav z-30 bg-[#FFF9F1] border-b border-amber-100" data-testid="cc-report-nav">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-2.5 flex gap-2 overflow-x-auto no-scrollbar">
            {/* v4.2 Subject Layer — first in the result navigation, opens the
                immersive pathway-style subject experience for this exact
                package + result tag. */}
            <ClaritySubjectsButton pkg={pkg} topTag={top.tag} />
            {navChips.map((c) => {
              const CIcon = c.icon;
              return (
                <button
                  key={c.id}
                  onClick={() => scrollTo(c.id)}
                  className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 bg-white border border-gray-200 hover:border-teal-300 hover:text-teal-700 rounded-full px-3.5 py-1.5 transition-colors"
                >
                  <CIcon size={12} /> {c.label}
                </button>
              );
            })}
          </div>
        </div>

        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-10 space-y-6">
          {/* 2. Top 2 alternative matches */}
          {alts.length > 0 && (
            <SectionCard icon={Compass} title="Other branches that suit you too" accent="amber" testId="cc-section-alts" id="cc-alts">
              <div className="space-y-3">
                {alts.map((p) => (
                  <details key={p.tag} className="border-2 border-amber-200 rounded-2xl bg-white px-4 py-3">
                    <summary className="cursor-pointer font-bold text-amber-900 list-none flex items-center justify-between gap-3">
                      <span>{p.title}</span>
                      <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0"><ChevronRight size={13} className="text-amber-700" /></span>
                    </summary>
                    <p className="text-sm text-gray-700 mt-2">{p.best_fit_for_students_who && `Best fit for students who ${p.best_fit_for_students_who}.`} {p.what_this_means}</p>
                  </details>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Subject Layer gateway — pathway-style dark card, below the
              alternative branches */}
          <ClaritySubjectsGateway pkg={pkg} topTag={top.tag} />

          {/* 3. Why this path matches you */}
          <SectionCard icon={Sparkles} title="Why this path matches you" accent="teal" testId="cc-section-why" id="cc-why">
            <p className="text-base md:text-lg">
              Your answers across{' '}
              <b>{matchedTests.length > 0 ? matchedTests.join(', ') : 'these tests'}</b>
              {' '}pointed strongly toward <b>{top.title}</b>
              {branchNames[top.tag] ? ` (${branchNames[top.tag]})` : ''}.
            </p>
            {top.best_fit_for_students_who && (
              <p className="text-sm md:text-base text-gray-700 mt-2">In short — you {top.best_fit_for_students_who}.</p>
            )}
            <div className="mt-4">
              <LexiTrigger
                variant="card"
                prefill={`My career-clarity report says my top match is "${top.title}". Can you deep-dive: what should I learn first, what salary to expect in India, and one starter project I can build this month?`}
                label="Ask Lexi to deep-dive your top match"
                description="Lexi reads your report + gives India-specific next steps"
                testId="cc-report-lexi"
              />
            </div>
          </SectionCard>

          {/* 4. What this branch/specialization means */}
          {top.what_this_means && (
            <SectionCard icon={BookOpen} title="What this branch means" accent="cyan" testId="cc-section-meaning" id="cc-meaning">
              <p>{top.what_this_means}</p>
            </SectionCard>
          )}

          {/* 5. What students will study */}
          {top.what_students_study && (
            <SectionCard icon={GraduationCap} title="What you will study" accent="purple" testId="cc-section-study" id="cc-study">
              <Pills items={top.what_students_study} tone="purple" />
            </SectionCard>
          )}

          {/* 6. Career opportunities */}
          {top.career_paths && (
            <SectionCard icon={Briefcase} title="Career opportunities" accent="emerald" testId="cc-section-careers" id="cc-careers">
              <Pills items={top.career_paths} tone="emerald" />
            </SectionCard>
          )}

          {/* 7. How AI is changing this field */}
          {top.how_ai_is_changing_this_field && (
            <SectionCard icon={TrendingUp} title="How AI is changing this field" accent="indigo" testId="cc-section-ai-changing" id="cc-ai">
              <p>{top.how_ai_is_changing_this_field}</p>
            </SectionCard>
          )}

          {/* 8. What work AI may automate */}
          {top.work_ai_may_automate && (
            <SectionCard icon={Cpu} title="What work AI may automate" accent="purple" testId="cc-section-ai-automate">
              <p className="text-gray-800">{top.work_ai_may_automate}</p>
              <p className="text-xs text-purple-700 mt-2 italic">Routine and repetitive work — not the role itself.</p>
            </SectionCard>
          )}

          {/* 9. AI tools and agentic AI to learn */}
          {top.ai_tools_and_agentic_ai_to_learn && (
            <SectionCard icon={Bot} title="AI tools & agentic AI skills to learn" accent="teal" testId="cc-section-ai-tools">
              <Pills items={top.ai_tools_and_agentic_ai_to_learn} tone="teal" />
            </SectionCard>
          )}

          {/* 10. Human knowledge still needed */}
          {top.human_knowledge_still_needed && (
            <SectionCard icon={ShieldCheck} title="Human knowledge still needed" accent="emerald" testId="cc-section-human-needed">
              <p className="text-gray-800">{top.human_knowledge_still_needed}</p>
              <p className="text-xs text-emerald-800 mt-2 italic">
                AI output must always be verified by humans with real domain knowledge.
              </p>
            </SectionCard>
          )}

          {/* 11. Beginner roadmap */}
          {top.beginner_roadmap && (
            <SectionCard icon={ListChecks} title="Beginner roadmap" accent="amber" testId="cc-section-roadmap" id="cc-roadmap">
              <Bullets items={top.beginner_roadmap} />
            </SectionCard>
          )}

          {/* 12. Mini-project idea */}
          {top.mini_project_idea && (
            <SectionCard icon={Lightbulb} title="Mini-project to start with" accent="rose" testId="cc-section-project" id="cc-project">
              <p className="text-gray-800">{top.mini_project_idea}</p>
            </SectionCard>
          )}

          {/* 13. Future Work with AI Agents — render ALL 5 subkeys */}
          {top.future_work_with_ai_agents && (
            <section id="cc-future" className="rounded-3xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-5 md:p-7 shadow-sm scroll-mt-48" data-testid="cc-section-future-ai-agents">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-11 h-11 rounded-2xl bg-indigo-500 text-white flex items-center justify-center shadow-sm"><Bot size={21} /></span>
                <h3 className="text-xl md:text-2xl font-black text-gray-900">Future Work with AI Agents in This Field</h3>
              </div>
              <p className="text-xs text-indigo-700 uppercase tracking-widest font-bold mb-4">Your branch · digital coworkers ahead</p>

              {/* a) how_future_work_may_look — narrative paragraph */}
              {top.future_work_with_ai_agents.how_future_work_may_look && (
                <div className="mb-5 bg-white/70 rounded-2xl border border-indigo-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Eye size={15} className="text-indigo-700" />
                    <p className="font-bold text-indigo-900">How future work may look</p>
                  </div>
                  <p className="text-gray-800 leading-relaxed text-sm md:text-base" data-testid="cc-fw-look">
                    {top.future_work_with_ai_agents.how_future_work_may_look}
                  </p>
                </div>
              )}

              {/* b) tasks_ai_agents_may_handle */}
              {top.future_work_with_ai_agents.tasks_ai_agents_may_handle && (
                <div className="mb-5 bg-white/70 rounded-2xl border border-indigo-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Cpu size={15} className="text-indigo-700" />
                    <p className="font-bold text-indigo-900">Tasks AI agents may handle</p>
                  </div>
                  <Bullets items={top.future_work_with_ai_agents.tasks_ai_agents_may_handle} />
                </div>
              )}

              {/* c) what_the_student_will_supervise */}
              {top.future_work_with_ai_agents.what_the_student_will_supervise && (
                <div className="mb-5 bg-white/70 rounded-2xl border border-emerald-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck size={15} className="text-emerald-700" />
                    <p className="font-bold text-emerald-900">What you will supervise as the human</p>
                  </div>
                  <Bullets items={top.future_work_with_ai_agents.what_the_student_will_supervise} />
                </div>
              )}

              {/* d) new_ai_agent_services_that_may_appear */}
              {top.future_work_with_ai_agents.new_ai_agent_services_that_may_appear && (
                <div className="mb-5 bg-white/70 rounded-2xl border border-amber-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Wrench size={15} className="text-amber-700" />
                    <p className="font-bold text-amber-900">New AI-agent services that may appear</p>
                  </div>
                  <Bullets items={top.future_work_with_ai_agents.new_ai_agent_services_that_may_appear} />
                </div>
              )}

              {/* e) how_to_prepare_for_ai_agent_teamwork */}
              {top.future_work_with_ai_agents.how_to_prepare_for_ai_agent_teamwork && (
                <div className="bg-white/70 rounded-2xl border border-rose-100 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <HandHeart size={15} className="text-rose-700" />
                    <p className="font-bold text-rose-900">How to prepare to work confidently with AI agents</p>
                  </div>
                  <Bullets items={top.future_work_with_ai_agents.how_to_prepare_for_ai_agent_teamwork} />
                </div>
              )}
            </section>
          )}

          {/* 14. Reality check — availability in India + nearby alternatives */}
          {top.reality_check && (
            <SectionCard icon={Eye} title="Reality check" accent="amber" testId="cc-section-reality" id="cc-reality">
              {Object.entries(top.reality_check).map(([k, v]) => (
                <div key={k} className="mb-3 last:mb-0">
                  <p className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-1.5">{k.replace(/_/g, ' ')}</p>
                  {Array.isArray(v) ? <Bullets items={v} /> : <p className="text-gray-800">{v}</p>}
                </div>
              ))}
            </SectionCard>
          )}

          {/* AI Awareness — repeated as reinforcement */}
          <AiAwarenessBlock awareness={pkg.package_level_ai_awareness_view} />

          {/* Actions */}
          {(() => {
            const exhausted = !!attempts && !attempts.is_admin && attempts.used >= attempts.max;
            return (
              <div className="cc-no-print flex flex-col gap-2 pt-2 items-center justify-center">
                <Button onClick={handleStartOver} disabled={exhausted} variant="outline" className="border-2 border-gray-300 rounded-2xl font-bold" data-testid="cc-startover-btn">
                  <RotateCcw size={14} className="mr-2" /> Retake quiz
                </Button>
                {exhausted ? (
                  <p className="text-xs font-semibold text-amber-700" data-testid="cc-retake-note">All attempts used</p>
                ) : attempts ? (
                  <p className="text-xs text-gray-500" data-testid="cc-retake-note">
                    {Math.max(0, attempts.max - attempts.used)} of {attempts.max} attempts left
                  </p>
                ) : null}
              </div>
            );
          })()}
        </section>
      </div>
    );
  }

  return null;
};

export default CareerClarity;
